import { MEMBERSHIP_TIMING, RTC_PROTOCOL_V1, RTC_SESSION_EVENTS } from '../protocol/constants.js';
import { RtcError, silentLogger, toRtcError, } from '../protocol/types.js';
import { hashedRoomName, memberIdFor } from '../identity/identity.js';
import { TypedEmitter } from '../util/emitter.js';
const asIntent = (value) => value === 'audio' || value === 'video' ? value : undefined;
export class JsSdkSignalling {
    constructor(options) {
        this.options = options;
        this.emitter = new TypedEmitter();
        this.subscriptions = [];
        this.state = 'idle';
        this.disposed = false;
        this.ownMembershipSeen = false;
        const userId = options.client.getUserId();
        const deviceId = options.client.getDeviceId();
        if (!userId || !deviceId) {
            throw new RtcError('unauthorized', 'Matrix client has no user or device id');
        }
        const room = options.client.getRoom(options.roomId);
        if (!room) {
            throw new RtcError('invalid_request', 'Room is not known to the Matrix client');
        }
        this.roomId = options.roomId;
        this.localUserId = userId;
        this.localDeviceId = deviceId;
        this.logger = options.logger ?? silentLogger;
        this.session = options.client.matrixRTC.getRoomSession(room);
        this.subscribe(RTC_SESSION_EVENTS.membershipsChanged, () => {
            this.trackOwnMembership();
            this.emitter.emit('participants', this.participants());
        });
        this.subscribe(RTC_SESSION_EVENTS.probablyLeft, ((probablyLeft) => {
            if (probablyLeft) {
                this.logger.warn('rtc.signalling.probably_left', { probablyLeft });
            }
            else {
                this.logger.info('rtc.signalling.probably_left', { probablyLeft });
            }
        }));
        this.subscribe(RTC_SESSION_EVENTS.joinStateChanged, ((joined) => {
            this.setState(joined ? 'joined' : this.state === 'error' ? 'error' : 'left');
        }));
        this.subscribe(RTC_SESSION_EVENTS.encryptionKeyChanged, ((key, index, membership, rtcBackendIdentity) => {
            this.emitter.emit('mediaKey', {
                livekitIdentity: rtcBackendIdentity,
                key,
                index,
                userId: membership.userId,
                deviceId: membership.deviceId,
            });
        }));
        this.subscribe(RTC_SESSION_EVENTS.membershipManagerError, ((error) => {
            this.logger.error('rtc.signalling.membership_error', { error: String(error) });
            this.setState('error');
            this.emitter.emit('error', toRtcError(error, 'membership_failed'));
        }));
        this.subscribe(RTC_SESSION_EVENTS.didSendCallNotification, ((content) => {
            if (typeof content?.event_id === 'string') {
                this.emitter.emit('ringSent', content.event_id);
            }
        }));
    }
    participants() {
        return this.session.memberships.map(membership => ({
            userId: membership.sender,
            deviceId: membership.deviceId,
            memberId: membership.memberId,
            livekitIdentity: membership.rtcBackendIdentity,
            intent: asIntent(membership.callIntent),
            joinedAt: membership.createdTs(),
            isLocal: this.isOwn(membership),
        }));
    }
    consensusIntent() {
        return asIntent(this.session.getConsensusCallIntent());
    }
    isJoined() {
        return this.session.isJoined();
    }
    async join(options) {
        this.assertUsable();
        this.resetOwnMembership();
        this.setState('joining');
        const transport = {
            type: options.transport.type,
            livekit_service_url: options.transport.livekit_service_url,
            livekit_alias: hashedRoomName(this.roomId),
        };
        try {
            this.startSession({
                userId: this.localUserId,
                deviceId: this.localDeviceId,
                memberId: memberIdFor(this.localUserId, this.localDeviceId),
            }, transport, {
                notificationType: options.notify === 'none' ? undefined : options.notify,
                notificationLifetimeMs: RTC_PROTOCOL_V1.ringLifetimeMs,
                callIntent: options.intent,
                manageMediaKeys: true,
                unstableSendStickyEvents: false,
                delayedLeaveEventDelayMs: MEMBERSHIP_TIMING.delayedLeaveEventDelayMs,
                delayedLeaveEventRestartMs: MEMBERSHIP_TIMING.delayedLeaveEventRestartMs,
                delayedLeaveEventRestartLocalTimeoutMs: MEMBERSHIP_TIMING.delayedLeaveEventRestartLocalTimeoutMs,
                maximumNetworkErrorRetryCount: MEMBERSHIP_TIMING.maximumNetworkErrorRetryCount,
                keyRotationGracePeriodMs: MEMBERSHIP_TIMING.keyRotationGracePeriodMs,
                useKeyDelay: MEMBERSHIP_TIMING.useKeyDelay,
            });
        }
        catch (error) {
            this.setState('error');
            throw toRtcError(error, 'membership_failed');
        }
    }
    async rejoin(options) {
        this.assertUsable();
        this.resetOwnMembership();
        try {
            await this.endSession(MEMBERSHIP_TIMING.rejoinLeaveTimeoutMs);
        }
        catch (error) {
            this.logger.warn('rtc.signalling.rejoin_leave_failed', { error: String(error) });
        }
        await this.join({ ...options, notify: 'none' });
    }
    async leave(timeoutMs = 5000) {
        this.resetOwnMembership();
        if (!this.session.isJoined()) {
            this.setState('left');
            return;
        }
        try {
            await this.endSession(timeoutMs);
        }
        finally {
            this.setState('left');
        }
    }
    async decline(notificationEventId) {
        this.assertUsable();
        await this.options.client.sendRtcDecline(this.roomId, notificationEventId);
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    dispose() {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        this.subscriptions.forEach(({ event, listener }) => this.session.off(event, listener));
        this.subscriptions.length = 0;
        this.emitter.removeAll();
    }
    get slotId() {
        return RTC_PROTOCOL_V1.slotId;
    }
    subscribe(event, listener) {
        this.session.on(event, listener);
        this.subscriptions.push({ event, listener });
    }
    startSession(identity, transport, config) {
        if (this.session.joinRTCSession) {
            this.session.joinRTCSession(identity, [], transport, config);
        }
        else if (this.session.join) {
            this.session.join(identity, [transport], config);
        }
        else {
            throw new RtcError('unknown', 'The Matrix RTC session cannot join');
        }
    }
    async endSession(timeoutMs) {
        if (this.session.leaveRoomSession) {
            return this.session.leaveRoomSession(timeoutMs);
        }
        if (this.session.leave) {
            return this.session.leave(timeoutMs);
        }
        throw new RtcError('unknown', 'The Matrix RTC session cannot leave');
    }
    isOwn(membership) {
        return membership.sender === this.localUserId && membership.deviceId === this.localDeviceId;
    }
    resetOwnMembership() {
        this.ownMembershipSeen = false;
        this.ownMembershipLostAt = undefined;
    }
    trackOwnMembership() {
        if (this.state !== 'joined') {
            return;
        }
        const present = this.session.memberships.some(membership => this.isOwn(membership));
        if (present) {
            if (this.ownMembershipLostAt !== undefined) {
                this.logger.info('rtc.signalling.own_membership_restored', { durationMs: Date.now() - this.ownMembershipLostAt });
                this.ownMembershipLostAt = undefined;
            }
            this.ownMembershipSeen = true;
        }
        else if (this.ownMembershipSeen && this.ownMembershipLostAt === undefined) {
            this.ownMembershipLostAt = Date.now();
            this.logger.warn('rtc.signalling.own_membership_lost');
        }
    }
    setState(next) {
        if (this.state === next) {
            return;
        }
        this.state = next;
        this.emitter.emit('joinState', next);
    }
    assertUsable() {
        if (this.disposed) {
            throw new RtcError('cancelled', 'Signalling was disposed');
        }
    }
}
